/* eslint-disable max-lines */
import React from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    providerParameterDefaults
} from 'builders/builderInternal/components/Common/providerConfigRegistry';
import {
    LlmFieldLabel
} from '../inline-steps/llmStepLayout';
import {
    getProviderParamHelp
} from './providerParameterHelp';

/**
 * ProviderParamEditor — schema-driven editor for the per-provider
 * `parameters` block. Reads the field set from
 * `providerParameterDefaults[provider]` so every key the existing
 * Instructions UI supports is exposed here too. Falls back to
 * editing the raw object when the provider isn't in the registry.
 *
 * Renders inputs by inferred type:
 *   number      → <Input type="number">
 *   boolean     → <Switch>
 *   array       → comma-separated chip-style input
 *   object      → JSON textarea
 *   string/enum → <Input> (decoding_method gets a Select)
 */

const ENUM_FIELDS = {
    decoding_method: ['greedy', 'sample']
};

export default function ProviderParamEditor({
    provider, value, onChange
}) {
    const defaults = providerParameterDefaults[provider];
    const params = value && typeof value === 'object' ? value : {};

    if (!defaults) {
        // Unknown provider — drop to raw JSON editor.
        return (
            <div>
                <LlmFieldLabel
                    label="Parameters (raw JSON)"
                    help="This provider is not in the built-in registry — edit the raw JSON object sent to the API."
                    helpTone="configuration"
                />
                <textarea
                    value={safeJSON(params)}
                    onChange={(e) => {
                        try { onChange(JSON.parse(e.target.value)); } catch { /* keep typing */ }
                    }}
                    className="min-h-[140px] w-full rounded-md border bg-background px-3 py-2 font-mono text-[12px]"
                />
                <p className="mt-1 text-[10.5px] text-muted-foreground">
                    Provider <code>{provider || '(unset)'}</code> not in registry — edit JSON directly.
                </p>
            </div>
        );
    }

    function patch(key, v) {
        onChange({
            ...params,
            [key]: v
        });
    }

    return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {Object.entries(defaults).map(([key, def]) => {
                const cur = key in params ? params[key] : def;
                return (
                    <FieldRow
                        key={key}
                        provider={provider}
                        fieldKey={key}
                        value={cur}
                        defaultValue={def}
                        onChange={(v) => patch(key, v)}
                    />
                );
            })}
        </div>
    );
}

function ParamFieldLabel({
    fieldKey, provider, suffix
}) {
    const help = getProviderParamHelp(provider, fieldKey);
    const label = `${prettyLabel(fieldKey)}${suffix || ''}`;
    return (
        <LlmFieldLabel
            label={label}
            help={help || `Setting passed to ${provider || 'the provider'} API: ${prettyLabel(fieldKey)}.`}
            helpTone="configuration"
        />
    );
}

function FieldRow({
    provider, fieldKey, value, defaultValue, onChange
}) {
    const inferredType = inferType(value, defaultValue);

    if (ENUM_FIELDS[fieldKey]) {
        return (
            <div>
                <ParamFieldLabel fieldKey={fieldKey} provider={provider} />
                <Select value={String(value ?? '')} onValueChange={(v) => onChange(v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {ENUM_FIELDS[fieldKey].map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        );
    }

    if (inferredType === 'boolean') {
        return (
            <div>
                <ParamFieldLabel fieldKey={fieldKey} provider={provider} />
                <div className="flex h-10 items-center gap-2 rounded-md border bg-card px-3">
                    <Switch checked={!!value} onCheckedChange={onChange} />
                    <span className="text-xs text-muted-foreground">{value ? 'on' : 'off'}</span>
                </div>
            </div>
        );
    }

    if (inferredType === 'number') {
        const step = isFloat(defaultValue) ? 0.05 : 1;
        return (
            <div>
                <ParamFieldLabel fieldKey={fieldKey} provider={provider} />
                <Input
                    type="number"
                    step={step}
                    value={value ?? ''}
                    onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') onChange(null);
                        else onChange(Number(raw));
                    }}
                    placeholder={String(defaultValue)}
                />
            </div>
        );
    }

    if (inferredType === 'array') {
        const arr = Array.isArray(value) ? value : [];
        return (
            <div>
                <ParamFieldLabel fieldKey={fieldKey} provider={provider} suffix=" (comma-separated)" />
                <Input
                    value={arr.join(', ')}
                    onChange={(e) => {
                        const parts = e.target.value
                            .split(',')
                            .map(s => s.trim())
                            .filter(s => s.length > 0);
                        onChange(parts);
                    }}
                    placeholder={Array.isArray(defaultValue) ? defaultValue.join(', ') : ''}
                />
            </div>
        );
    }

    if (inferredType === 'object') {
        return (
            <div className="md:col-span-2">
                <ParamFieldLabel fieldKey={fieldKey} provider={provider} suffix=" (JSON)" />
                <textarea
                    value={safeJSON(value ?? defaultValue)}
                    onChange={(e) => {
                        try { onChange(JSON.parse(e.target.value)); } catch { /* keep typing */ }
                    }}
                    className="min-h-[60px] w-full rounded-md border bg-background px-3 py-2 font-mono text-[11.5px]"
                />
            </div>
        );
    }

    // string / null / unknown
    return (
        <div>
            <ParamFieldLabel fieldKey={fieldKey} provider={provider} />
            <Input
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                placeholder={defaultValue == null ? '(none)' : String(defaultValue)}
            />
        </div>
    );
}

function inferType(v, def) {
    const sample = v != null ? v : def;
    if (typeof sample === 'boolean') return 'boolean';
    if (typeof sample === 'number') return 'number';
    if (Array.isArray(sample)) return 'array';
    if (sample && typeof sample === 'object') return 'object';
    if (typeof sample === 'string') return 'string';
    return 'string';
}

function isFloat(n) {
    return typeof n === 'number' && !Number.isInteger(n);
}

function prettyLabel(key) {
    return key.replace(/_/g, ' ');
}

function safeJSON(v) {
    try { return JSON.stringify(v ?? {}, null, 2); } catch { return '{}'; }
}
